import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Trash, ArrowsClockwise, EnvelopeSimple, FolderOpen, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MailboxesPanel — configure the mailboxes we ingest email from and,
 * optionally, which folders inside each mailbox we care about.
 *
 * Wiring: works against `settings.ms_mailboxes` (a List<MailboxConfig>).
 * The dirty-tracking / sticky-save bar in Settings.jsx picks up changes
 * automatically because we mutate `settings` via `setSettings`.
 */
export function MailboxesPanel({ settings, setSettings, isAdmin }) {
  const mailboxes = settings.ms_mailboxes || [];
  const [newAddress, setNewAddress] = useState("");
  // Folder discovery per-mailbox: { [address]: { loading, folders, error } }
  const [folderCache, setFolderCache] = useState({});
  const regions = (settings.regions || []).map((r) => r.name);

  const updateMailbox = (idx, patch) => {
    setSettings((s) => {
      const next = [...(s.ms_mailboxes || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...s, ms_mailboxes: next };
    });
  };

  const addMailbox = () => {
    const addr = newAddress.trim().toLowerCase();
    if (!addr || !addr.includes("@")) return;
    if (mailboxes.some((m) => m.address?.toLowerCase() === addr)) {
      toast.error("Mailbox already added");
      return;
    }
    setSettings((s) => ({
      ...s,
      ms_mailboxes: [
        ...(s.ms_mailboxes || []),
        { address: addr, enabled: true, scope: "inbox", folders: [] },
      ],
    }));
    setNewAddress("");
  };

  const removeMailbox = (idx) => {
    if (!window.confirm(`Remove mailbox "${mailboxes[idx]?.address}"? This won't delete any existing tickets — it just stops polling.`)) return;
    setSettings((s) => ({
      ...s,
      ms_mailboxes: (s.ms_mailboxes || []).filter((_, i) => i !== idx),
    }));
  };

  const discoverFolders = async (addr) => {
    setFolderCache((c) => ({ ...c, [addr]: { loading: true } }));
    try {
      const r = await api.get(`/settings/ms-mailboxes/${encodeURIComponent(addr)}/folders`);
      setFolderCache((c) => ({ ...c, [addr]: { loading: false, folders: r.data } }));
      toast.success(`Found ${r.data.length} folder${r.data.length === 1 ? "" : "s"}`);
    } catch (e) {
      const err = e?.response?.data?.detail || "Discovery failed";
      setFolderCache((c) => ({ ...c, [addr]: { loading: false, error: err } }));
      toast.error(err);
    }
  };

  const addFolder = (mbIdx, folder) => {
    setSettings((s) => {
      const next = [...(s.ms_mailboxes || [])];
      const cur = next[mbIdx];
      if ((cur.folders || []).some((f) => f.folder_id === folder.id)) return s;
      next[mbIdx] = {
        ...cur,
        folders: [
          ...(cur.folders || []),
          {
            folder_name: folder.displayName,
            folder_id: folder.id,
            region: regions.includes(folder.displayName) ? folder.displayName : "",
          },
        ],
      };
      return { ...s, ms_mailboxes: next };
    });
  };

  const updateFolder = (mbIdx, fIdx, patch) => {
    setSettings((s) => {
      const next = [...(s.ms_mailboxes || [])];
      const cur = next[mbIdx];
      const folders = [...(cur.folders || [])];
      folders[fIdx] = { ...folders[fIdx], ...patch };
      next[mbIdx] = { ...cur, folders };
      return { ...s, ms_mailboxes: next };
    });
  };

  const removeFolder = (mbIdx, fIdx) => {
    setSettings((s) => {
      const next = [...(s.ms_mailboxes || [])];
      const cur = next[mbIdx];
      next[mbIdx] = { ...cur, folders: (cur.folders || []).filter((_, i) => i !== fIdx) };
      return { ...s, ms_mailboxes: next };
    });
  };

  return (
    <div className="hairline rounded-lg p-5 bg-white space-y-4" data-testid="mailboxes-panel">
      <div>
        <div className="font-heading font-semibold flex items-center gap-2">
          <EnvelopeSimple size={16} weight="duotone" className="text-[#4f4c90]" /> Mailboxes
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Every mailbox listed here is polled for new email. Choose <b>whole inbox</b> to ingest everything, or <b>specific folders</b> when Outlook rules move messages into named folders (e.g. Australia, Americas).
        </p>
      </div>

      <div className="space-y-3">
        {mailboxes.length === 0 && (
          <div className="text-xs text-slate-500 italic border border-dashed border-slate-200 rounded-md p-3">
            No mailboxes configured yet. Add one below.
          </div>
        )}
        {mailboxes.map((mb, i) => {
          const cache = folderCache[mb.address] || {};
          return (
            <div key={mb.address} className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-3" data-testid={`mailbox-${mb.address}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-slate-800 font-mono">{mb.address}</span>
                <span className="chip !text-[10px] !py-0.5">{mb.scope === "folders" ? `${(mb.folders || []).length} folder${(mb.folders || []).length === 1 ? "" : "s"}` : "whole inbox"}</span>
                {mb.enabled ? (
                  <span className="chip status-in_progress !text-[10px] !py-0.5">enabled</span>
                ) : (
                  <span className="chip status-waiting !text-[10px] !py-0.5">disabled</span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {isAdmin && (
                    <>
                      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                        <Switch checked={!!mb.enabled} onCheckedChange={(v) => updateMailbox(i, { enabled: v })} data-testid={`mailbox-enable-${mb.address}`} /> Poll
                      </label>
                      <button
                        onClick={() => removeMailbox(i)}
                        className="text-red-500 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50"
                        data-testid={`mailbox-remove-${mb.address}`}
                        title="Remove mailbox"
                      >
                        <Trash size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Scope</Label>
                  <Select
                    value={mb.scope || "inbox"}
                    onValueChange={(v) => updateMailbox(i, { scope: v })}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid={`mailbox-scope-${mb.address}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="inbox">Whole inbox</SelectItem>
                      <SelectItem value="folders">Specific folders only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Team</Label>
                  <Select
                    value={mb.team || "systems"}
                    onValueChange={(v) => updateMailbox(i, { team: v })}
                    disabled={!isAdmin}
                  >
                    <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid={`mailbox-team-${mb.address}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="systems">Business Systems</SelectItem>
                      <SelectItem value="it">IT</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">Every ticket polled from this mailbox lands in the chosen team's queue.</p>
                </div>
              </div>

              {mb.scope === "folders" && (
                <div className="rounded-md border border-slate-200 bg-white p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1">
                      <FolderOpen size={12} weight="duotone" /> Folders to poll
                    </div>
                    {isAdmin && (
                      <button
                        onClick={() => discoverFolders(mb.address)}
                        disabled={cache.loading}
                        className="text-xs text-[#4f4c90] hover:underline flex items-center gap-1 disabled:opacity-50"
                        data-testid={`mailbox-discover-${mb.address}`}
                      >
                        <ArrowsClockwise size={12} className={cache.loading ? "animate-spin" : ""} />
                        {cache.loading ? "Discovering…" : "Discover folders"}
                      </button>
                    )}
                  </div>

                  {cache.error && (
                    <div className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 flex items-start gap-1.5">
                      <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
                      <span>{cache.error}</span>
                    </div>
                  )}

                  {(mb.folders || []).length === 0 && (
                    <div className="text-[11px] text-slate-500 italic">
                      No folders selected yet. {cache.folders ? "Pick one from the list below." : "Click \"Discover folders\" to load them from Microsoft Graph."}
                    </div>
                  )}

                  {(mb.folders || []).map((f, fi) => (
                    <div key={f.folder_id} className="rounded-md border border-slate-200 bg-slate-50/60 p-2 space-y-2" data-testid={`folder-${f.folder_id}`}>
                      <div className="flex items-center gap-2">
                        <FolderOpen size={12} weight="duotone" className="text-[#4f4c90]" />
                        <span className="text-sm font-medium">{f.folder_name}</span>
                        <span className="text-[10px] font-mono text-slate-400">{f.folder_id.slice(0, 10)}…</span>
                        {isAdmin && (
                          <button
                            onClick={() => removeFolder(i, fi)}
                            className="ml-auto text-red-500 hover:text-red-600 p-1 rounded-md hover:bg-red-50"
                            data-testid={`folder-remove-${f.folder_id}`}
                          ><Trash size={12} /></button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        <div>
                          <Label className="text-[10px] uppercase text-slate-500">Region tag</Label>
                          <Select
                            value={f.region || "__none__"}
                            onValueChange={(v) => updateFolder(i, fi, { region: v === "__none__" ? "" : v })}
                            disabled={!isAdmin}
                          >
                            <SelectTrigger className="mt-1 bg-white border-slate-200 h-8 text-xs"><SelectValue placeholder="— none —" /></SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                              <SelectItem value="__none__">— none —</SelectItem>
                              {regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}

                  {cache.folders && cache.folders.length > 0 && (
                    <div className="border-t border-slate-100 pt-2">
                      <div className="text-[10px] uppercase text-slate-500 mb-1">Add from mailbox</div>
                      <div className="flex flex-wrap gap-1">
                        {cache.folders
                          .filter((fld) => !(mb.folders || []).some((x) => x.folder_id === fld.id))
                          .map((fld) => (
                            <button
                              key={fld.id}
                              onClick={() => addFolder(i, fld)}
                              disabled={!isAdmin}
                              className="chip !text-slate-700 !border-slate-300 bg-white hover:bg-[#4f4c90]/10 hover:!text-[#4f4c90] hover:!border-[#4f4c90]/30 !text-[11px] !py-0.5 flex items-center gap-1"
                              data-testid={`folder-add-${fld.id}`}
                            >
                              <Plus size={10} /> {fld.displayName}
                              {fld.totalItemCount > 0 && (
                                <span className="text-[10px] text-slate-400 font-mono ml-1">({fld.totalItemCount})</span>
                              )}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isAdmin && (
        <div className="border-t border-slate-100 pt-3 flex gap-2">
          <Input
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
            placeholder="new-mailbox@auroraenergy.co"
            className="bg-white border-slate-200 h-9 font-mono text-sm flex-1"
            data-testid="mailbox-new-address"
          />
          <Button
            onClick={addMailbox}
            className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-9 gap-1"
            data-testid="mailbox-add"
          >
            <Plus size={14} /> Add mailbox
          </Button>
        </div>
      )}

      <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2 flex items-start gap-1.5">
        <CheckCircle size={12} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
        <span>
          For each new mailbox to work end-to-end, the Entra app registration needs <b>Mail.ReadWrite</b> + <b>Mail.Send</b> scoped to that mailbox via an Application Access Policy. See KB → <i>Azure Multi-Mailbox Configuration</i> for the step-by-step walkthrough. (Per-alias replies are currently disabled — replies go from the base mailbox address.)
        </span>
      </div>
    </div>
  );
}
